import React, { useCallback, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Alert, Box, Button, Link, Skeleton, Typography } from '@mui/material';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import { START_PAYMENT_METHOD_UPDATE } from '../../graphql/billingQueries';
import type { PaymentProviderName } from '../../hooks/useActivePaymentProvider';
import EmptyState from '../ui/EmptyState';
import Section from '../ui/Section';
import { executeBillingLaunch } from './BillingProviderLauncher';
import type { BillingInfo } from './billingTypes';

const PORTAL_FAILED = "We couldn't open the card settings. Please try again, or contact support if this keeps happening.";

/**
 * Opens Stripe's billing portal, where the customer updates the card on their
 * subscription. PithPlay never collects or stores card details.
 */
export function usePaymentPortal({
  provider,
  billing,
  returnPath,
}: {
  provider: PaymentProviderName | null;
  billing: BillingInfo | null | undefined;
  /** Where Stripe sends the customer back to: the page they started from. */
  returnPath: string;
}) {
  const [startPaymentMethodUpdate, { loading }] = useMutation(START_PAYMENT_METHOD_UPDATE);
  const [error, setError] = useState<string | null>(null);
  const hasCustomer = Boolean(billing?.providerCustomerId);
  const available = provider === 'stripe' && hasCustomer;

  const open = useCallback(async () => {
    setError(null);
    if (provider !== 'stripe' || !hasCustomer) {
      setError("Your card can't be changed here. Email info@artbits.solutions and we'll update it for you.");
      return;
    }
    try {
      const { data } = await startPaymentMethodUpdate({
        variables: { returnUrl: `${window.location.origin}${returnPath}` },
      });
      const result = data?.startPaymentMethodUpdate;
      if (!result?.success || !result?.launch) {
        setError(PORTAL_FAILED);
        return;
      }
      const launched = executeBillingLaunch(result.launch, { onUnsupported: () => setError(PORTAL_FAILED) });
      if (!launched) setError(PORTAL_FAILED);
    } catch (err) {
      console.error('Payment method update error:', err);
      setError(PORTAL_FAILED);
    }
  }, [hasCustomer, provider, returnPath, startPaymentMethodUpdate]);

  return { open, opening: loading, error, clearError: () => setError(null), available };
}

export type PaymentPortal = ReturnType<typeof usePaymentPortal>;

interface PaymentMethodSectionProps {
  billing: BillingInfo | null | undefined;
  /** A subscription bills this account (see summarizeBilling). */
  hasLiveSubscription: boolean;
  provider: PaymentProviderName | null;
  providerLoading: boolean;
  onRetryProvider?: () => void;
  portal: PaymentPortal;
  /** Opens the plan picker (Free accounts, and Moneris card changes). */
  onSeePlans: () => void;
  /** Section title; null for a page whose header already names it. */
  title?: string | null;
  id?: string;
}

/**
 * The card behind the subscription. Free accounts have nothing to manage
 * here: no card actions, just the way to the plans.
 */
const PaymentMethodSection: React.FC<PaymentMethodSectionProps> = ({
  billing,
  hasLiveSubscription,
  provider,
  providerLoading,
  onRetryProvider,
  portal,
  onSeePlans,
  title = 'Payment method',
  id,
}) => {
  const hasCustomer = Boolean(billing?.providerCustomerId);

  let body: React.ReactNode;
  if (providerLoading) {
    body = (
      <Box role="status" aria-label="Loading payment method">
        <Skeleton width="70%" />
        <Skeleton width="45%" />
      </Box>
    );
  } else if (!provider) {
    body = (
      <Alert
        severity="warning"
        action={
          onRetryProvider ? (
            <Button color="inherit" size="small" onClick={onRetryProvider}>
              Retry
            </Button>
          ) : undefined
        }
      >
        We couldn&apos;t load your payment settings. Try again, or contact support if this keeps happening.
      </Alert>
    );
  } else if (!hasLiveSubscription) {
    body = (
      <EmptyState
        compact
        icon={<CreditCardOutlinedIcon />}
        title={hasCustomer ? 'No active subscription' : 'No card on file'}
        description="The Free plan doesn't need a card. You add one when you upgrade."
        secondaryActionLabel="See plans"
        onSecondaryAction={onSeePlans}
      />
    );
  } else if (provider === 'moneris') {
    body = (
      <>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The card for this subscription is set at checkout. To use a different card, change your plan.
        </Typography>
        <Button variant="outlined" color="inherit" onClick={onSeePlans}>
          Change plan
        </Button>
      </>
    );
  } else if (!hasCustomer) {
    body = (
      <Typography variant="body2" color="text.secondary">
        This subscription&apos;s card can&apos;t be changed here. Email{' '}
        <Link href="mailto:info@artbits.solutions?subject=Update%20my%20card">info@artbits.solutions</Link> and we&apos;ll
        update it for you.
      </Typography>
    );
  } else {
    body = (
      <>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 2 }}>
          <CreditCardOutlinedIcon sx={{ fontSize: 20, color: 'text.secondary', mt: '2px' }} aria-hidden="true" />
          <Typography variant="body2" color="text.secondary">
            Your card is held by Stripe, our payment provider. PithPlay never stores your card number.
          </Typography>
        </Box>
        <Button variant="outlined" color="inherit" onClick={() => void portal.open()} disabled={portal.opening}>
          {portal.opening ? 'Opening…' : 'Update card'}
        </Button>
      </>
    );
  }

  return (
    <Section title={title ?? undefined} id={id} sx={{ height: '100%' }}>
      {portal.error && (
        <Alert severity="error" onClose={portal.clearError} sx={{ mb: 2 }}>
          {portal.error}
        </Alert>
      )}
      {body}
    </Section>
  );
};

export default PaymentMethodSection;
